import React, { useState, ReactElement } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  SignalCellMark,
  HexFieldBackdrop,
  PropensityStrip,
} from './brand/BrandMarks';

interface AuthPageProps {
  onSuccess?: () => void;
}

export const LoginPage: React.FC<AuthPageProps> = ({
  onSuccess,
}): ReactElement => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await login(email, password);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const errorMessage = (err as any)?.response?.data?.detail || 'Login failed';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-dvh bg-bg-base flex items-center justify-center">
      <HexFieldBackdrop origin="center" />
      <div className="relative bg-bg-raised border border-border-base rounded-lg shadow-ds-xl w-full max-w-md overflow-hidden">
        <PropensityStrip />
        <div className="p-8">
        <div className="flex items-center gap-2.5 mb-2">
          <SignalCellMark size={30} />
          <h1 className="text-3xl font-bold text-fg-primary">
            Voterra
          </h1>
        </div>
        <p className="text-fg-muted mb-6">Campaign Command Center</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-fg-secondary font-semibold mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              required
              className="
                w-full px-4 py-2 bg-bg-elevated border border-border-subtle rounded
                text-fg-primary placeholder-fg-muted focus:outline-none
                disabled:opacity-50
              "
              placeholder="your@email.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-fg-secondary font-semibold mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              required
              className="
                w-full px-4 py-2 bg-bg-elevated border border-border-subtle rounded
                text-fg-primary placeholder-fg-muted focus:outline-none
                disabled:opacity-50
              "
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div className="bg-status-danger-bg/20 border border-status-danger-bg rounded p-3 text-status-danger text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="
              w-full px-4 py-2 bg-accent hover:bg-accent-hover text-white
              font-bold rounded transition-colors disabled:opacity-50
            "
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <p className="text-center text-fg-muted text-sm mt-4">
          Don't have an account?{' '}
          <a href="/register" className="text-accent-fg hover:text-accent-fg/80 font-bold">
            Register here
          </a>
        </p>
        </div>
      </div>
    </div>
  );
};

export const RegisterPage: React.FC<AuthPageProps> = ({
  onSuccess,
}): ReactElement => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [fullName, setFullName] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { register } = useAuth();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      await register({ email, password, full_name: fullName });
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const errorMessage = (err as any)?.response?.data?.detail || 'Registration failed';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-dvh bg-bg-base flex items-center justify-center">
      <HexFieldBackdrop origin="center" />
      <div className="relative bg-bg-raised border border-border-base rounded-lg shadow-ds-xl w-full max-w-md overflow-hidden">
        <PropensityStrip />
        <div className="p-8">
        <div className="flex items-center gap-2.5 mb-2">
          <SignalCellMark size={30} />
          <h1 className="text-3xl font-bold text-fg-primary">
            Create Account
          </h1>
        </div>
        <p className="text-fg-muted mb-6">Voterra — Campaign Command Center</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="fullName" className="block text-fg-secondary font-semibold mb-2">
              Full Name
            </label>
            <input
              id="fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              disabled={isLoading}
              required
              className="
                w-full px-4 py-2 bg-bg-elevated border border-border-subtle rounded
                text-fg-primary placeholder-fg-muted focus:outline-none
                disabled:opacity-50
              "
              placeholder="John Smith"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-fg-secondary font-semibold mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              required
              className="
                w-full px-4 py-2 bg-bg-elevated border border-border-subtle rounded
                text-fg-primary placeholder-fg-muted focus:outline-none
                disabled:opacity-50
              "
              placeholder="your@email.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-fg-secondary font-semibold mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              required
              className="
                w-full px-4 py-2 bg-bg-elevated border border-border-subtle rounded
                text-fg-primary placeholder-fg-muted focus:outline-none
                disabled:opacity-50
              "
              placeholder="••••••••"
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-fg-secondary font-semibold mb-2">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              required
              className="
                w-full px-4 py-2 bg-bg-elevated border border-border-subtle rounded
                text-fg-primary placeholder-fg-muted focus:outline-none
                disabled:opacity-50
              "
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div className="bg-status-danger-bg/20 border border-status-danger-bg rounded p-3 text-status-danger text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="
              w-full px-4 py-2 bg-accent hover:bg-accent-hover text-white
              font-bold rounded transition-colors disabled:opacity-50
            "
          >
            {isLoading ? 'Creating account...' : 'Register'}
          </button>
        </form>

        <p className="text-center text-fg-muted text-sm mt-4">
          Already have an account?{' '}
          <a href="/login" className="text-accent-fg hover:text-accent-fg/80 font-bold">
            Login here
          </a>
        </p>
        </div>
      </div>
    </div>
  );
};
